import { GRID_SIZE, squareKey } from './grid';
import type { Coordinate, Square } from './types';

/*
 * The rules behind the grid screen, as data. No Vue, no fetch.
 *
 * Every one of these is enforced by the backend inside its claim transaction — see the API's
 * docs/GAME_RULES.md. They exist here so the screen can disable a button and say why, rather
 * than let somebody tap through to a 409. When the two disagree, the backend wins: it is the
 * one holding the condition expression.
 */

/**
 * Mirrors MAX_SQUARES_PER_REQUEST in the API's domain/games.ts. A claim is `n + 2` items in one
 * DynamoDB transaction and the limit is 100, so 25 leaves room to spare.
 */
export const MAX_SQUARES_PER_REQUEST = 25;

export interface AllowanceInput {
  maxSquaresPerPlayer: number;
  alreadyHeld: number;
  freeSquares: number;
}

/**
 * How many more squares this player may claim right now — the smallest of what the game allows
 * them, what one request may carry, and what is actually left on the grid.
 *
 * Floored at zero: a host reassignment or a lowered limit can leave somebody above the maximum,
 * and a negative allowance would make every comparison in the view behave strangely.
 */
export function remainingAllowance(input: AllowanceInput): number {
  const underLimit = input.maxSquaresPerPlayer - input.alreadyHeld;
  return Math.max(0, Math.min(underLimit, MAX_SQUARES_PER_REQUEST, input.freeSquares));
}

/** What a selection costs. Whole currency units — see `formatMoney`. */
export const selectionCost = (count: number, pricePerSquare: number): number =>
  count * pricePerSquare;

/**
 * What "pick for me" should offer. The host's recommendation is the starting point, less
 * whatever the player already holds, clamped to what they are actually allowed.
 *
 * A player already past the recommendation is offered one rather than nothing: the
 * recommendation is advice, and `maxSquaresPerPlayer` is the rule.
 */
export function suggestedAutoCount(
  game: { recommendedSquaresPerPlayer?: number | undefined; maxSquaresPerPlayer: number },
  held: { alreadyHeld: number; freeSquares: number },
): number {
  const allowance = remainingAllowance({
    maxSquaresPerPlayer: game.maxSquaresPerPlayer,
    ...held,
  });
  if (allowance === 0) return 0;

  const towardsRecommendation = (game.recommendedSquaresPerPlayer ?? 0) - held.alreadyHeld;

  return Math.min(allowance, Math.max(1, towardsRecommendation));
}

/**
 * `count` squares nobody holds, chosen at random.
 *
 * Only local play needs this. A player's own "pick for me" is `POST /squares/auto`, which chooses
 * on the server for whoever is calling — and the host picking on somebody else's behalf has no
 * way to be that caller. So the choosing happens here and the squares go through the override
 * route, which keeps one copy of the arithmetic that moves the pot.
 *
 * Being client-side costs nothing that matters: the randomness is a convenience for somebody
 * with no favourite square, not a fairness guarantee, and the backend still refuses anything
 * already held.
 */
export function randomFreeSquares(squares: Square[], count: number): Coordinate[] {
  if (count <= 0) return [];

  const taken = new Set(squares.map((square) => squareKey(square.col, square.row)));
  const free: Coordinate[] = [];
  for (let col = 0; col < GRID_SIZE; col += 1) {
    for (let row = 0; row < GRID_SIZE; row += 1) {
      if (!taken.has(squareKey(col, row))) free.push({ col, row });
    }
  }

  // Partial Fisher-Yates: shuffle only as far as needed, and take the front. Sampling with
  // retries would spin on a nearly full grid, which is exactly when this is used at a party.
  const wanted = Math.min(count, free.length);
  for (let i = 0; i < wanted; i += 1) {
    const j = i + Math.floor(Math.random() * (free.length - i));
    [free[i], free[j]] = [free[j] as Coordinate, free[i] as Coordinate];
  }

  return free.slice(0, wanted);
}

/** Pulls the conflict list out of an ApiError's `details`, or undefined if it is not there. */
export function conflictsFrom(details: unknown): Coordinate[] | undefined {
  const conflicts = (details as { conflicts?: unknown } | undefined)?.conflicts;
  return Array.isArray(conflicts) ? (conflicts as Coordinate[]) : undefined;
}
